import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  styled
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RootState, AppDispatch } from '../store/store';
import { createProjectAsync, fetchProjects, fetchProject } from '../store/projectSlice';
import { DEFAULT_PROJECT } from '../constants/defaults';
import styles from './Sidebar.module.css';
import clsx from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  position: 'fixed',
  top: 12,
  left: 12,
  zIndex: 1200,
  '@media (max-width: 900px)': {
    display: 'block',
  },
}));

const SidebarContainer = styled(Box)<{ open: boolean }>(({ theme, open }) => ({
  width: 260,
  backgroundColor: '#e7e4e4',
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 72px)',
  position: 'fixed',
  left: 0,
  top: 72,
  zIndex: 1100,
  transition: 'transform 0.3s ease-in-out',
  '@media (max-width: 900px)': {
    width: '100%',
    transform: open ? 'translateX(0)' : 'translateX(-100%)',
  },
}));

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { projects, selectedProject, loading, error } = useSelector((state: RootState) => state.projects);
  
  // Reference to the currently selected project item for scrolling
  const selectedProjectRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  // Effect to scroll the selected project into view when it changes
  useEffect(() => {
    if (selectedProject && selectedProjectRef.current) {
      // Use scrollIntoView with behavior: 'smooth' for a better user experience
      selectedProjectRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest' // Scroll the minimal amount needed
      });
    }
  }, [selectedProject?.id]);

  const handleProjectSelect = async (project: any) => {
    console.log('Sidebar - handleProjectSelect called with project:', project);
    if (project.id) {
      console.log('Sidebar - dispatching fetchProject with id:', project.id);
      const result = await dispatch(fetchProject(project.id));
      console.log('Sidebar - fetchProject result:', result);
      
      // Always navigate to home page when selecting a project
      navigate('/');
      
      if (window.innerWidth <= 900) {
        onToggle(); // Close sidebar on mobile after selection
      }
    }
  };

  const handleNewProject = async () => {
    const result = await dispatch(createProjectAsync({
      name: `${DEFAULT_PROJECT.name} ${projects.length + 1}`,
      base_prompt: DEFAULT_PROJECT.base_prompt,
      model_name: DEFAULT_PROJECT.model_name
    }));

    if (createProjectAsync.fulfilled.match(result) && result.payload.id) {
      await dispatch(fetchProject(result.payload.id));
      if (window.innerWidth <= 900) {
        onToggle(); // Close sidebar on mobile after creating new project
      }
    }
  };

  if (loading && projects.length === 0) {
    return (
      <SidebarContainer open={isOpen}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </SidebarContainer>
    );
  }

  if (error) {
    return (
      <SidebarContainer open={isOpen}>
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => dispatch(fetchProjects())}
              >
                <RefreshIcon />
              </IconButton>
            }
          >
            {error}
          </Alert>
        </Box>
      </SidebarContainer>
    );
  }

  return (
    <>
      <MobileMenuButton
        onClick={onToggle}
        size="large"
        edge="start"
        color="inherit"
        aria-label="menu"
      >
        <MenuIcon />
      </MobileMenuButton>

      <SidebarContainer open={isOpen}>
        <Box className={styles.header}>
          <h2 className={styles.headerTitle}>Projects</h2>
          <IconButton
            onClick={handleNewProject}
            size="small"
            className={styles.addButton}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : <AddIcon />}
          </IconButton>
        </Box>
        {projects.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No projects yet. Click the + button to create one.
            </Typography>
          </Box>
        ) : (
          <List className={styles.projectList}>
            {[...projects]
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((project) => (
              <ListItem
                key={project.id}
                disablePadding
                className={styles.projectItem}
                ref={selectedProject?.id === project.id ? selectedProjectRef : null}
              >
                <ListItemButton
                  onClick={() => handleProjectSelect(project)}
                  className={clsx(
                    styles.projectButton,
                    selectedProject?.id === project.id && styles.projectButtonSelected
                  )}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {selectedProject?.id === project.id && (
                      <CheckCircleIcon 
                        fontSize="small" 
                        color="primary" 
                        sx={{ mr: 1, flexShrink: 0 }} 
                      />
                    )}
                    <ListItemText
                      primary={project.name}
                      secondary={project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : 'No date'}
                      primaryTypographyProps={{
                        className: styles.projectName,
                        component: 'h3',
                      }}
                      secondaryTypographyProps={{
                        className: styles.projectDate,
                      }}
                    />
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
